import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import type { ResolveItem } from "../lib/types.ts";
import { usePlayerPlan, type DisplayCommand } from "./usePlayerPlan.ts";
import { registerPlayerSW } from "./registerSW.ts";
import Slide from "./Slide.tsx";
import { DEFAULT_SLIDE_SECONDS } from "../lib/defaults.ts";
import "./player.css";

/** How long an image slide may take to load + decode before it is skipped. */
const IMAGE_READY_TIMEOUT_MS = 20_000;

// /display/:id?token=… — the only page the TVs load. Runs the resolution loop,
// plays the current plan on a timer, and can never go blank: any gap, outage, or
// cold start falls back to the bundled slide.
export default function Player() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const preview = params.get("preview") === "1";

  const [blanked, setBlanked] = useState(false);

  // Remote display commands (reload/refresh handled in the hook; here we own
  // the visual blank state). refresh is a no-op here — the hook re-resolves.
  const onCommand = useCallback((action: DisplayCommand) => {
    if (action === "reload") window.location.reload();
    else if (action === "blank") setBlanked(true);
    else if (action === "unblank") setBlanked(false);
  }, []);

  const { plan, status } = usePlayerPlan(id, token, onCommand, preview);
  const items = plan?.items ?? [];
  const [index, setIndex] = useState(0);

  // Register the offline service worker once (production only). Skipped in the
  // dashboard preview iframe — it shouldn't claim the SW scope or cache.
  useEffect(() => {
    if (!preview) registerPlayerSW();
  }, [preview]);

  // Restart the loop whenever the plan content changes.
  useEffect(() => {
    setIndex(0);
  }, [plan?.revision]);

  const showFallback = status === "fallback" || items.length === 0;
  const current = showFallback ? null : items[index % items.length];
  const currentKey = `${plan?.revision}-${index}`;

  // Keep the last two slides mounted so the incoming one crossfades OVER the
  // outgoing one — no black gap between slides.
  const [layers, setLayers] = useState<{ key: string; item: ResolveItem }[]>([]);
  const hasLayers = layers.length > 0;

  // Readiness gate: an image slide goes on screen only once its picture is
  // fetched AND decoded. Mounting it earlier fades in an empty <img> on the
  // black slide background — usually for a blink, but a slow or failed fetch
  // of a multi-MB deck page left a TV on a black slide for its whole duration.
  // Until it's ready the outgoing slide simply stays up; one that fails (or
  // stalls past the timeout) is skipped. With nothing on screen yet, or only
  // one item, there is nothing better to hold, so it is shown regardless.
  const [readyKey, setReadyKey] = useState<string | null>(null);
  useEffect(() => {
    if (!current) return;
    if (current.type !== "image" || !current.url) {
      setReadyKey(currentKey);
      return;
    }
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      if (ok || !hasLayers || items.length < 2) setReadyKey(currentKey);
      else setIndex((i) => (i + 1) % items.length);
    };
    const img = new Image();
    img.src = current.url;
    img.decode().then(
      () => finish(true),
      () => finish(false),
    );
    const t = setTimeout(() => finish(false), IMAGE_READY_TIMEOUT_MS);
    return () => {
      settled = true;
      clearTimeout(t);
    };
    // hasLayers/items.length are read at the moment of failure only; re-running
    // on them would restart a load that's already in flight.
  }, [currentKey, current?.url]);
  const ready = readyKey === currentKey;

  // Advance on each item's duration, counted from when it actually appeared.
  useEffect(() => {
    if (!ready || !current) return;
    const ms = (current.durationSeconds || DEFAULT_SLIDE_SECONDS) * 1000;
    const t = setTimeout(
      () => setIndex((i) => (i + 1) % items.length),
      ms,
    );
    return () => clearTimeout(t);
  }, [ready, currentKey, items.length]);

  // Preload the next image so it's decoded before it's shown (smoother swaps).
  useEffect(() => {
    if (items.length < 2) return;
    const next = items[(index + 1) % items.length];
    if (next?.type === "image" && next.url) {
      const img = new Image();
      img.src = next.url;
    }
  }, [index, items]);

  useEffect(() => {
    if (!current) {
      setLayers([]);
      return;
    }
    if (!ready) return; // hold the outgoing slide until this one can paint
    setLayers((prev) => {
      const top = prev[prev.length - 1];
      if (top && top.key === currentKey) {
        // Same slide. If the plan was just re-signed (fresh SAS URLs), swap the
        // fresh item into the current layer in place — no crossfade — so even a
        // single-slide display picks up non-expired URLs.
        if (top.item === current) return prev;
        const copy = prev.slice();
        copy[copy.length - 1] = { key: currentKey, item: current };
        return copy;
      }
      return [...prev, { key: currentKey, item: current }].slice(-2);
    });
  }, [currentKey, current, ready]);

  return (
    <div className="player-root" data-orientation={plan?.orientation ?? "landscape"}>
      {/* Also covers a cold start whose first image is still downloading. */}
      {showFallback || !hasLayers ? (
        <img
          className="slide slide-media"
          src="/fallback-slide.svg"
          alt="Liberty Bible Church"
        />
      ) : (
        layers.map((l) => (
          <div className="slide-fade" key={l.key}>
            <Slide item={l.item} />
          </div>
        ))
      )}

      {blanked && <div className="player-blank" />}

      {!token && (
        <div className="player-warn">
          No display token. Expected /display/{id}?token=…
        </div>
      )}
    </div>
  );
}
